/**
 * Quando ABRIR o painel precisa pedir uma conversa.
 *
 * UM caso só: não há conversa nenhuma nesta página. É a primeira abertura, ou
 * a volta de quem não tinha nada guardado — a bolha existe, a conversa nasce no
 * clique.
 *
 * **OLHAR NÃO É RECOMEÇAR**, e essa distinção custou dois relatos seguidos.
 *
 * Antes, `encerrada` também pedia sessão aqui, e pedia sem `reabrirApenas`: era
 * indistinguível de um clique em conversa nenhuma. O primeiro relato foi a
 * saudação nascendo sozinha embaixo do "Conversa encerrada.". O segundo veio do
 * outro lado do balcão: o atendente finalizava, a pessoa recarregava a loja e
 * abria o chat só para reler a despedida — e a conversa VOLTAVA para a lista
 * "ATIVAS" dele, com o mesmo `queue_id`, porque o servidor rodava
 * `reativarConversa`. Finalizar não segurava: bastava a pessoa espiar.
 *
 * Reler a conversa encerrada agora não escreve nada. O cliente segue vivo
 * (`onClosed` não o descarta), a tela mostra o histórico e o aviso, e o campo de
 * texto continua aberto — quem recomeça é quem ESCREVE, pelo `sendText`, que
 * enfileira o texto e pede a sessão que pode criar.
 */
export function precisaDeConversaAoAbrir(estado: {
  /** O painel está aberto neste instante? */
  open: boolean
  /** Já existe um cliente conectado? */
  temCliente: boolean
}): boolean {
  if (!estado.open) return false
  return !estado.temCliente
}

/**
 * A saudacao gravada pelo servidor quando a conversa NASCE.
 *
 * Ela e da abertura do chat, nao de toda conversa nova: quando a anterior
 * acabou e outra nasce no meio do assunto, a saudacao da nova entrava na tela
 * depois do que a pessoa acabou de escrever — como se o chat tivesse recomecado
 * do zero na frente dela.
 *
 * O reconhecimento e pelo ID que o servidor emite (`chatweb-greeting-<uuid>`),
 * nao pelo texto: mudar a saudacao nas configuracoes nao quebra isto.
 */
export function ehSaudacao(message: { id?: string | null } | null | undefined): boolean {
  return typeof message?.id === "string" && message.id.startsWith("chatweb-greeting-")
}

/**
 * Onde o historico entra na tela.
 *
 * Tres casos, e o terceiro so existe porque o historico chega DEPOIS do socket:
 *
 * 1. Tela vazia (primeira abertura) — o historico E a tela.
 * 2. Tela com conversa — o que faltava entra NA ORDEM DO SERVIDOR, cada linha
 *    logo depois da anterior a ela que ja esta na tela (ver `encaixarNaOrdem`).
 *    Conversa nova depois de uma encerrada nao tem nada em comum com a tela, e
 *    por isso entra inteira DEPOIS: a pessoa continua vendo o que combinou.
 * 3. Tela so com AVISO — o historico entra ANTES dele.
 *
 * O caso 2 ja foi "sempre no fim", e isso errava a reabertura: reabrir uma
 * conversa finalizada devolve a MESMA linha do chat, e o texto guardado sai
 * assim que o socket conecta — muitas vezes antes de o historico chegar. A
 * despedida que tinha se perdido ia parar DEPOIS do que a pessoa acabou de
 * escrever. Visto no Chrome em 29/09/2026.
 *
 * O caso 3 e o do recarregamento sobre uma conversa finalizada: o servidor
 * manda `closed` assim que o socket conecta, e esse aviso chega enquanto o
 * `GET /chatweb/messages` ainda esta vindo. Sem esta regra, a tela abria com
 * "Conversa encerrada." no topo e a conversa inteira embaixo — o fim antes do
 * comeco. Aviso e do agora; historico e de antes.
 *
 * Duas coisas nunca entram nos casos 2 e 3:
 *
 * - a SAUDACAO gravada no servidor (ver `ehSaudacao`);
 * - o que JA ESTA na tela. Reabrir uma conversa finalizada devolve a MESMA
 *   linha do chat, entao o historico que chega e o mesmo que a pessoa esta
 *   lendo — sem conferir o id, abrir o painel depois de recarregar mostrava a
 *   conversa inteira duas vezes.
 */
export function juntarHistorico<A extends EntradaDaTela, N extends EntradaDaTela>(
  anteriores: A[],
  novas: N[],
): Array<A | N> {
  if (!anteriores.length) return novas

  if (anteriores.every((entrada) => entrada.kind === "system")) {
    // Sem mensagem na tela nao ha o que repetir: so a saudacao fica de fora.
    return [...novas.filter((entrada) => !ehSaudacaoNaTela(entrada)), ...anteriores]
  }

  return encaixarNaOrdem(anteriores, novas)
}

/**
 * O caso 2 de `juntarHistorico`: o que falta na tela, na ordem do servidor.
 *
 * A ordem vem do SERVIDOR, nao do relogio: cada linha que faltava entra logo
 * depois da anterior a ela no historico que ja esta na tela. Assim a resposta
 * perdida fica acima do "Um atendente entrou na conversa." que chegou depois
 * dela, e o relogio do visitante (que carimba o eco local) nao embaralha nada.
 * Sem nenhuma anterior na tela, entra antes da primeira seguinte que esta — ou
 * no fim, se nenhuma estiver.
 *
 * O eco otimista (`local-…`) nao vira duas bolhas: a versao do servidor toma o
 * lugar dele, como faz o `pushMessage` quando o eco chega pelo socket.
 *
 * Nada a acrescentar devolve a MESMA lista, para a tela nao redesenhar a toa.
 */
function encaixarNaOrdem<A extends EntradaDaTela, N extends EntradaDaTela>(
  tela: A[],
  historico: N[],
): Array<A | N> {
  const resultado: Array<A | N> = tela.slice()
  let mudou = false
  // Onde esta, na tela, a ultima linha do historico ja percorrida.
  let ancora = -1

  historico.forEach((entrada, i) => {
    if (entrada.kind !== "message") return
    const id = idDaMensagem(entrada)

    const naTela = id === null ? -1 : resultado.findIndex((linha) => idDaMensagem(linha) === id)
    if (naTela >= 0) {
      ancora = naTela
      return
    }

    const eco = indiceDoEco(resultado, entrada.message)
    if (eco >= 0) {
      resultado[eco] = entrada
      ancora = eco
      mudou = true
      return
    }

    if (ehSaudacaoNaTela(entrada)) return

    let onde = ancora + 1
    if (ancora < 0) {
      const seguintes = new Set(
        historico
          .slice(i + 1)
          .map(idDaMensagem)
          .filter((idSeguinte): idSeguinte is string => idSeguinte !== null),
      )
      const seguinte = resultado.findIndex((linha) => {
        const idNaTela = idDaMensagem(linha)
        return idNaTela !== null && seguintes.has(idNaTela)
      })
      onde = seguinte >= 0 ? seguinte : resultado.length
    }

    // Sem id nao da para conferir repeticao — e melhor mostrar do que sumir.
    resultado.splice(onde, 0, entrada)
    ancora = onde
    mudou = true
  })

  return mudou ? resultado : tela
}

/** O eco local (`local-…`) que a versao do servidor desta mensagem substitui. */
function indiceDoEco(tela: EntradaDaTela[], message: MensagemDaTela): number {
  if (message.direction !== "inbound" || typeof message.text !== "string") return -1
  return tela.findIndex(
    (linha) =>
      linha.kind === "message" &&
      typeof linha.message.id === "string" &&
      linha.message.id.startsWith("local-") &&
      linha.message.direction === "inbound" &&
      linha.message.text === message.text,
  )
}

/**
 * O minimo que `juntarHistorico` precisa saber de uma linha da tela: se e
 * mensagem ou aviso e, sendo mensagem, o id (e, para reconhecer o eco local,
 * direcao e texto). Nao e o `Entry` do widget de proposito: assim o teste monta
 * um caso com duas chaves, e nao com um `ChatMessage` inteiro so para exercitar
 * a ordem.
 */
type MensagemDaTela = { id?: string | null; direction?: string; text?: string }

type EntradaDaTela = { kind: "message"; message: MensagemDaTela } | { kind: "system" }

function ehSaudacaoNaTela(entrada: EntradaDaTela): boolean {
  return entrada.kind === "message" && ehSaudacao(entrada.message)
}

function idDaMensagem(entrada: EntradaDaTela): string | null {
  if (entrada.kind !== "message") return null
  const id = entrada.message.id
  return typeof id === "string" && id ? id : null
}
